// src/components/ui/EtatProbleme.tsx — SCE Issues
// L'état d'un problème dans les listes (02/10) : la criticité en points, le
// statut en cercle, sans libellé — il apparaît au survol.
//
// Maquette : https://claude.ai/artifact/7ug228nDoWUkuqaMVD1geS
//
// ─── Pourquoi plus de pastilles dans les listes ───
//
// Deux pastilles pleines par ligne disaient deux fois la même chose (« Ouvert »
// et « À qualifier » : c'est la qualification qui fait passer en cours), et
// mettaient côte à côte deux verts (Mineur, Clos) et le jaune des interactions
// (Majeur). Ici une seule couleur vive par famille : le rouge pour Critique, le
// vert pour Clos. Tout le reste est en noir et gris.
//
// - Criticité : ●●● rouges = Critique, ●● noirs = Majeur, ● gris = Mineur.
//   Pas de points tant que le problème n'est pas qualifié (ni pour un annulé).
// - Statut : cercle en pointillés = à qualifier (Ouvert), horloge = en cours,
//   coche verte = clos, cercle barré = annulé.
//
// La fiche détail garde ses badges : en tête de fiche, le mot compte plus que
// la place.

import { Infobulle } from '@/components/ui/Infobulle'
import { A_QUALIFIER, type Criticite, type Statut } from '@/types/issue.types'

/** Le statut tel qu'on le dit dans les listes : « Ouvert » veut dire « à qualifier ». */
export const LIBELLE_STATUT_LISTE: Record<Statut, string> = {
  Ouvert: 'À qualifier',
  'En cours': 'En cours',
  Clos: 'Clos',
  Annulé: 'Annulé',
}

const NIVEAU: Partial<Record<Criticite, number>> = { Critique: 3, Majeur: 2, Mineur: 1 }
const COULEUR: Partial<Record<Criticite, string>> = {
  Critique: 'var(--color-traffic-red)',
  Majeur: 'var(--color-black)',
  Mineur: 'var(--color-gray-70)',
}

/** Trois points, dont 1 à 3 pleins. Rien pour « À qualifier ». */
export function PointsCriticite({ criticite }: { criticite: Criticite }): JSX.Element {
  const n = NIVEAU[criticite] ?? 0
  const couleur = COULEUR[criticite] ?? 'var(--color-gray-50)'
  return (
    <svg width="20" height="16" viewBox="0 0 20 16" aria-hidden="true" style={{ flex: 'none' }}>
      {n > 0 &&
        [3.5, 10, 16.5].map((x, i) => (
          <circle key={x} cx={x} cy="8" r="2.8" fill={i < n ? couleur : 'var(--color-gray-20)'} />
        ))}
    </svg>
  )
}

export function IconeStatut({ statut }: { statut: Statut }): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" style={{ flex: 'none' }}>
      {statut === 'Ouvert' && (
        <circle cx="8" cy="8" r="6.2" fill="none" stroke="var(--color-gray-50)" strokeWidth="1.6" strokeDasharray="2.2 2" />
      )}
      {statut === 'En cours' && (
        <>
          <circle cx="8" cy="8" r="6.2" fill="none" stroke="var(--color-black)" strokeWidth="1.6" />
          <path d="M8 4.6V8l2.4 1.6" fill="none" stroke="var(--color-black)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
      {statut === 'Clos' && (
        <>
          <circle cx="8" cy="8" r="7" fill="var(--color-traffic-green)" />
          <path d="M4.8 8.2 7 10.3 11.2 5.8" fill="none" stroke="var(--color-white)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
      {statut === 'Annulé' && (
        <>
          <circle cx="8" cy="8" r="6.2" fill="none" stroke="var(--color-gray-50)" strokeWidth="1.6" />
          <path d="M4 12 12 4" stroke="var(--color-gray-50)" strokeWidth="1.6" strokeLinecap="round" />
        </>
      )}
    </svg>
  )
}

/** Criticité affichée : rien tant que le problème n'est pas qualifié, ni pour un annulé. */
function criticiteAffichee(statut: Statut, criticite: Criticite): Criticite | null {
  if (statut === 'Ouvert' || statut === 'Annulé' || criticite === A_QUALIFIER) return null
  return criticite
}

/**
 * Points + cercle, libellés au survol (PC) et pour les lecteurs d'écran. La
 * place des points est toujours réservée : d'une ligne à l'autre, les cercles
 * restent alignés.
 */
export function EtatProbleme({ statut, criticite }: { statut: Statut; criticite: Criticite }): JSX.Element {
  const crit = criticiteAffichee(statut, criticite)
  const libelle = LIBELLE_STATUT_LISTE[statut]
  return (
    <Infobulle titre={libelle} detail={crit ? `Criticité : ${crit}` : undefined} className="etat-probleme">
      {crit ? <PointsCriticite criticite={crit} /> : <span className="etat-probleme-vide" aria-hidden="true" />}
      <IconeStatut statut={statut} />
      <span className="sr-only">{crit ? `${libelle}, criticité ${crit}` : libelle}</span>
    </Infobulle>
  )
}

/** Avec les mots (colonnes Criticité et Statut du tableau, listes de filtres). */
export function CriticiteLibellee({ criticite }: { criticite: Criticite }): JSX.Element {
  if (criticite === A_QUALIFIER) return <span className="etat-libelle etat-libelle--attente">À qualifier</span>
  return (
    <span className="etat-libelle">
      <PointsCriticite criticite={criticite} />
      {criticite}
    </span>
  )
}

export function StatutLibelle({ statut }: { statut: Statut }): JSX.Element {
  return (
    <span className={'etat-libelle' + (statut === 'Ouvert' || statut === 'Annulé' ? ' etat-libelle--attente' : '')}>
      <IconeStatut statut={statut} />
      {LIBELLE_STATUT_LISTE[statut]}
    </span>
  )
}
